<template>
	<step
		:name="name"
		:title="title"
		:valid="isLast"
		:set-active="position.before"
		:position="setPosition">
		<template v-if="!loadUploader">
			<div class="alertbox alertbox--error alertbox mt-s mb-m">
				<svg class="alertbox__icon alertbox__icon--error">
					<use xlink:href="#alert--error" />
				</svg>
				<p class="alertbox__text">
					<strong>Let op:</strong> Voor dit product is het niet mogelijk om te uploaden.
					<span
					class="calculate-button link cursor-pointer">Klik hier om terug te gaan</span>.
				</p>
			</div>
		</template>

		<template v-else>

			<div class="description mv-s" v-if="selectedFormat.id">
				<p class="description__text">
					Neem voor het uploaden de aanleverspecificaties goed door.
					Dit voorkomt problemen met het uploaden.
				</p>
				<modal
					title="Aanleverspecificaties"
					button-style="descriptionButton"
					modal-style="styled"
					button-text="Aanleverspecificaties"
					v-if="!isConfiguration && !isOffTheShelf">
					<delivery-specification :format="selectedFormat" overview />
				</modal>
			</div>

			<div class="description description--vertical mv-s" v-else>
				<p class="description__text">
					<template v-if="availableFormats">
						Neem voor het uploaden de aanleverspecificaties goed door.
						Dit voorkomt problemen met het uploaden.
					</template>
					<template v-else-if="!checkData.hasSelectedProduct">
						Om te kunnen uploaden dient er eerst een product geselecteerd te worden.
						<span class="calculate-button cursor-pointer click-here-to-choose-product">Klik hier om een product te kiezen</span>.
					</template>
				</p>
				<div class="description__link-container" v-if="!getConfiguratorData.hasFormatSuggestions && checkData.hasSelectedProduct">
					<modal
						v-for="(format, index) in availableFormats"
						:key="index"
						title="Aanleverspecificaties"
						button-style="custom"
						button-class="link description__link"
						:button-text="format.productSize.fullName">
						<delivery-specification :format="format" />
					</modal>
				</div>
			</div>

			<div class="grid grid--ms-full grid--gutters" v-if="checkData.hasSelectedProduct && onlyCoverRequired">
				<!-- Hybrid off-the-shelf: the only file is the cover, posted as the content upload -->
				<upload-field
					:is-last="isLast"
					name="Upload omslag"
					type="content"
					id="cover"
					:position="position"/>
			</div>
			<div class="grid grid--ms-full grid--gutters" v-else-if="checkData.hasSelectedProduct">
				<upload-field
					:separate-cover="hasSeparateCover"
					:is-last="isLast"
					name="Upload binnenwerk"
					type="content"
					id="content"
					:position="position"/>
				<upload-field
					v-if="hasSeparateCover"
					:separate-cover="hasSeparateCover"
					:is-last="isLast"
					name="Upload omslag"
					type="cover"
					id="cover"
					:position="position"/>
			</div>
			<div
				class="description description--vertical mv-s"
				v-if="getConfiguratorData.hasFormatSuggestions">
				<p
					class="description__text">Uw geüploade bestand komt niet overeen met onze formaten. Onderstaand een aantal suggesties voor formaten die dicht in de buurt komen. Pas uw bestand hier op aan en probeer het nogmaals.</p>
				<div class="description__link-container">
					<modal
						v-for="(format, index) in getFormatSuggestions"
						:key="index"
						title="Aanleverspecificaties"
						button-style="custom"
						button-class="link description__link"
						:button-text="format.productSize.fullName">
						<delivery-specification :format="format" />
					</modal>
				</div>
			</div>
			<div v-if="getMatchedCustomSizeFormat !== null && !isLast" class="mt-m mb-m">
				<format-custom-choice
					:has-suggestion="getConfiguratorData.hasFormatSuggestions"
					:option="getMatchedCustomSizeFormatData"
					type="uploader"/>
			</div>
			<div class="cell cell--full" v-if="getUploadHasError">
				<div class="wetransfer-upload background-indigo-025 text--center">
					<div class="grid grid--full">
						<div class="cell">
							<h3 class="mb-s">Problemen met uploaden?</h3>
							<p
								v-if="isLast">Plaats uw bestelling dan eerst, kies voor de optie PDF-controle en lever vervolgens uw bestanden aan (bij achteraf uploaden kan dit via WeTransfer). Wij geven u vervolgens feedback om het bestand geschikt voor productie te maken.</p>
							<p v-else>
								<span
									class="link cursor-pointer"
									@click="toConfigurator">Configureer uw product eerst</span> en plaats uw bestelling, kies voor de optie PDF-controle en lever vervolgens uw bestanden aan (bij achteraf uploaden kan dit via WeTransfer). Wij geven u vervolgens feedback om het bestand geschikt voor productie te maken.
							</p>
						</div>
					</div>
				</div>
			</div>
		</template>
	</step>
</template>

<script>
import { CONFIG } from "@/config.js";
import step from "@/components/step/step";
import UploadField from "./upload-field";
import UploadSuggestion from "./upload-suggestion";
import modal from "@/components/element/modal";
import deliverySpecification from "@/components/information/delivery-specification";
import formatCustomChoice from "@/components/format/format-custom-choice";

export default {
	name: "Uploader",
	components: {
		step,
		UploadField,
		UploadSuggestion,
		modal,
		deliverySpecification,
		formatCustomChoice
	},
	data () {
		return {
			name: "uploader",
			canUpload: true
		};
	},
	computed: {
		isConfiguration () {
			return this.$store.getters.getConfiguratorType.isConfiguration;
		},
		isOffTheShelf () {
			return this.$store.getters.isOffTheShelf;
		},
		selectedFormat () {
			return this.$store.getters.getSelectedFormat;
		},
		configuratorData () {
			return this.$store.getters.getConfiguratorData;
		},
		position () {
			return this.$store.getters.getUploaderPosition;
		},
		setPosition () {
			return this.position.before ? 0 : null;
		},
		loadUploader () {
			if (this.$store.getters.isOffTheShelf) {
				return false;
			}

			if (this.position.after) {
				return this.$store.getters.isConfiguratorDataValid;
			}

			return true;
		},
		title () {
			let name = "Upload uw bestand";
			if (this.hasSeparateCover && !this.onlyCoverRequired) {
				name += "en";
			}
			return name;
		},
		hasEditorUrl () {
			return this.$store.getters.hasEditorUrl;
		},
		hasSeparateCover () {
			return this.$store.getters.hasSeparateCover;
		},
		onlyCoverRequired () {
			return this.$store.getters.onlyCoverRequired;
		},
		isLast () {
			return !this.position.before;
		},
		stepIsUploader () {
			return this.$store.getCurrentStep.data.name === "uploader";
		},
		availableFormats () {
			return this.$store.getters.getAvailableFormats;
		},
		getConfiguratorData () {
			return this.$store.getters.getConfiguratorData;
		},
		getFormatSuggestions () {
			return this.$store.getters.getFormatSuggestions;
		},
		getUploadHasError () {
			return this.$store.getters.getUploadHasError;
		},
		getMatchedCustomSizeFormat () {
			return this.$store.getters.getMatchedCustomSizeFormat;
		},
		getMatchedCustomSizeFormatData () {
			return this.$store.getters.getMatchedCustomSizeFormatData;
		},
		uploadsAreComplete () {
			return this.$store.getters.uploadsAreComplete;
		},
		checkData () {
			return this.$store.getters.checkData;
		}
	},
	created () {
		const existingPublicId = this.$store.getters.getUploadSessionGuid;
		const selectedFormat = this.$store.getters.getSelectedFormat;
		const specificationId = selectedFormat && selectedFormat.id ? selectedFormat.id : null;
		const selectedProduct = this.$store.getters.getSelectedProduct;
		const productFrontId = selectedProduct ? selectedProduct.id : this.$store.state.baseProps.productFrontId[0];
		const customSize = this.$store.getters.getCustomSize;
		const pageCount = this.$store.getters.getPageCount;

		let params = [];
		if (existingPublicId) {
			params.push(`publicId=${existingPublicId}`);
		}
		if (specificationId) {
			params.push(`specificationId=${specificationId}`);
		}
		if (productFrontId) {
			params.push(`productFrontId=${productFrontId}`);
		}
		if (customSize && customSize.width) {
			params.push(`customWidth=${customSize.width}`);
		}
		if (customSize && customSize.height) {
			params.push(`customHeight=${customSize.height}`);
		}
		if (pageCount) {
			params.push(`pageCount=${pageCount}`);
		}

		const url = CONFIG.HOST + "configurator/create-configuration/" + (params.length > 0 ? "?" + params.join("&") : "");

		this.$http.get(url).then(result => {
			this.$store.commit("SET_UPLOAD_SESSION_GUID", result.body);
		});
	},
	methods: {
		toConfigurator () {
			this.$store.commit("RESET_ALL_FILE_UPLOADS");
			this.$store.commit("SET_CONFIGURATOR_TYPE", "calculate");
		},
		handleSuccess () {
			this.$store.commit("SET_STEP_VALID");
		}
	}
};
</script>

<style lang="scss">
.separate-cover-label {
  font-size: 1.6rem;
  font-weight: 300;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.wetransfer-upload {
  padding: 30px;
  background-color: #f0f0f0;
  border-radius: 5px;
  margin-top: 20px;
}
.click-here-to-choose-product {
	font-size: 1.3rem;
	text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/uploader/uploader.vue